import React, { useState, useEffect, useCallback, useMemo, useRef } from "react";
import ReactQuill from "react-quill";
import "react-quill/dist/quill.snow.css";
import {
  Box,
  Typography,
  Button,
  TextField,
  Paper,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Chip,
  IconButton,
  CircularProgress,
  Snackbar,
  MenuItem,
  Tooltip,
  InputAdornment,
} from "@material-ui/core";
import { makeStyles } from "@material-ui/core/styles";
import {
  Add,
  Delete,
  Edit,
  VideoLibrary,
  Description,
  School,
  Search,
  PlayCircleOutline,
  MenuBook,
  ChevronRight,
  FolderOpen,
} from "@material-ui/icons";
import { Alert } from "@material-ui/lab";
import { useAuth } from "../context/ApiKeyContext";
import { useTranslation } from "../hooks/useTranslation";
import { getApiBaseUrl } from "../config";

const API_BASE_URL = getApiBaseUrl();
const KB_EDIT_RULES = ["knowledge_base_edit", "admin"];

const useStyles = makeStyles((theme) => ({
  page: {
    display: "flex",
    flexDirection: "column",
    height: "calc(100vh - 64px)",
    overflow: "hidden",
    background: "var(--ikaro-bg-page)",
  },

  /* ─── Header ─────────────────────────────── */
  pageHeader: {
    padding: "20px 28px 16px",
    background: "var(--ikaro-bg-surface)",
    borderBottom: "1px solid var(--ikaro-border-default)",
    display: "flex",
    alignItems: "center",
    justifyContent: "space-between",
    flexShrink: 0,
  },
  pageHeaderLeft: {
    display: "flex",
    alignItems: "center",
    gap: 12,
  },
  pageIcon: {
    width: 40,
    height: 40,
    borderRadius: 10,
    background: "var(--ikaro-orange-light)",
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    "& svg": { color: "var(--ikaro-orange-primary)", fontSize: 22 },
  },
  pageTitle: {
    fontWeight: 700,
    fontSize: "1.25rem",
    color: "var(--ikaro-text-primary)",
    lineHeight: 1.2,
  },
  pageSubtitle: {
    fontSize: "0.78rem",
    color: "var(--ikaro-text-secondary)",
    marginTop: 2,
  },

  /* ─── Body (3 colunas) ───────────────────── */
  body: {
    display: "flex",
    flex: 1,
    overflow: "hidden",
  },

  /* ─── Coluna 1: Categorias ───────────────── */
  catPanel: {
    width: 220,
    flexShrink: 0,
    display: "flex",
    flexDirection: "column",
    background: "var(--ikaro-bg-surface)",
    borderRight: "1px solid var(--ikaro-border-default)",
    overflow: "hidden",
  },
  catPanelHeader: {
    padding: "14px 16px 10px",
    display: "flex",
    alignItems: "center",
    justifyContent: "space-between",
    borderBottom: "1px solid var(--ikaro-border-default)",
  },
  catPanelTitle: {
    fontWeight: 600,
    fontSize: "0.8rem",
    textTransform: "uppercase",
    letterSpacing: "0.06em",
    color: "var(--ikaro-text-secondary)",
  },
  catList: {
    flex: 1,
    overflowY: "auto",
    padding: "8px 8px",
  },
  catItem: {
    display: "flex",
    alignItems: "center",
    justifyContent: "space-between",
    padding: "7px 10px",
    borderRadius: 8,
    cursor: "pointer",
    marginBottom: 2,
    transition: "background 0.15s",
    "&:hover": {
      background: "var(--ikaro-orange-light)",
      "& $catItemActions": { opacity: 1 },
    },
  },
  catItemActive: {
    background: "var(--ikaro-orange-light) !important",
    "& $catItemLabel": {
      color: "var(--ikaro-orange-primary)",
      fontWeight: 700,
    },
  },
  catItemLabel: {
    fontSize: "0.85rem",
    color: "var(--ikaro-text-primary)",
    flex: 1,
    overflow: "hidden",
    textOverflow: "ellipsis",
    whiteSpace: "nowrap",
  },
  catItemActions: {
    display: "flex",
    opacity: 0,
    transition: "opacity 0.15s",
  },

  /* ─── Coluna 2: Lista de artigos ─────────── */
  artPanel: {
    width: 320,
    flexShrink: 0,
    display: "flex",
    flexDirection: "column",
    borderRight: "1px solid var(--ikaro-border-default)",
    background: "var(--ikaro-bg-page)",
    overflow: "hidden",
  },
  artPanelHeader: {
    padding: "12px 14px 10px",
    background: "var(--ikaro-bg-surface)",
    borderBottom: "1px solid var(--ikaro-border-default)",
  },
  artPanelSearch: {
    marginTop: 8,
  },
  artList: {
    flex: 1,
    overflowY: "auto",
    padding: "8px",
    display: "flex",
    flexDirection: "column",
    gap: 6,
  },
  artCard: {
    background: "var(--ikaro-bg-surface)",
    borderRadius: 10,
    padding: "10px 12px",
    cursor: "pointer",
    border: "2px solid transparent",
    transition: "all 0.15s",
    "&:hover": {
      borderColor: "var(--ikaro-orange-light)",
      boxShadow: "var(--ikaro-shadow-light)",
      "& $artCardActions": { opacity: 1 },
    },
  },
  artCardActive: {
    borderColor: "var(--ikaro-orange-primary) !important",
    boxShadow: "var(--ikaro-shadow-medium) !important",
  },
  artCardTop: {
    display: "flex",
    alignItems: "flex-start",
    justifyContent: "space-between",
    gap: 6,
  },
  artCardTitle: {
    fontWeight: 600,
    fontSize: "0.85rem",
    color: "var(--ikaro-text-primary)",
    lineHeight: 1.35,
    flex: 1,
  },
  artCardMeta: {
    display: "flex",
    alignItems: "center",
    gap: 6,
    marginTop: 5,
  },
  artCardCat: {
    fontSize: "0.72rem",
    color: "var(--ikaro-text-secondary)",
  },
  artCardActions: {
    display: "flex",
    opacity: 0,
    transition: "opacity 0.15s",
  },
  chipVideo: {
    background: "var(--ikaro-purple-light)",
    color: "var(--ikaro-purple-accent)",
    "& .MuiChip-icon": { color: "var(--ikaro-purple-accent)" },
  },
  chipText: {
    background: "var(--ikaro-status-running-bg)",
    color: "var(--ikaro-status-running-text)",
    "& .MuiChip-icon": { color: "var(--ikaro-status-running-text)" },
  },
  emptyState: {
    display: "flex",
    flexDirection: "column",
    alignItems: "center",
    justifyContent: "center",
    flex: 1,
    padding: 24,
    gap: 8,
    color: "var(--ikaro-text-muted)",
    "& svg": { fontSize: 44, color: "var(--ikaro-text-muted)" },
  },

  /* ─── Coluna 3: Visualização ─────────────── */
  viewPanel: {
    flex: 1,
    display: "flex",
    flexDirection: "column",
    overflow: "hidden",
    background: "var(--ikaro-bg-page)",
  },
  viewContent: {
    flex: 1,
    overflowY: "auto",
    padding: "24px 32px",
    background: "var(--ikaro-bg-page)",
  },
  viewEmpty: {
    flex: 1,
    display: "flex",
    flexDirection: "column",
    alignItems: "center",
    justifyContent: "center",
    gap: 10,
    color: "var(--ikaro-text-muted)",
    "& svg": { fontSize: 64, color: "var(--ikaro-text-muted)" },
  },
  viewTitle: {
    fontWeight: 700,
    fontSize: "1.4rem",
    color: "var(--ikaro-text-primary)",
    marginBottom: 10,
    lineHeight: 1.3,
  },
  viewMeta: {
    display: "flex",
    alignItems: "center",
    gap: 8,
    marginBottom: 20,
    paddingBottom: 16,
    borderBottom: "1px solid var(--ikaro-border-default)",
  },
  viewBody: {
    /* Usa o wrapper nativo do Quill para que ql-align-* e demais classes funcionem */
    "& .ql-editor": {
      padding: 0,
      minHeight: "unset",
      lineHeight: 1.75,
      fontSize: "0.93rem",
      color: "var(--ikaro-text-primary)",
    },
    "& .ql-editor img": { maxWidth: "100%", borderRadius: 8, marginTop: 8 },
    "& .ql-editor a": { color: "var(--ikaro-orange-primary)" },
    "& .ql-editor h1, & .ql-editor h2, & .ql-editor h3": {
      color: "var(--ikaro-text-primary)",
      marginTop: 16,
      marginBottom: 8,
    },
    "& .ql-editor ul, & .ql-editor ol": { paddingLeft: 24, marginBottom: 8 },
    "& .ql-editor p": { marginBottom: 6 },
    "& .ql-editor pre, & .ql-editor code": {
      background: "var(--ikaro-bg-surface-secondary)",
      borderRadius: 6,
      padding: "2px 6px",
      fontSize: "0.85em",
    },
    "& .ql-editor blockquote": {
      borderLeft: "3px solid var(--ikaro-orange-primary)",
      paddingLeft: 12,
      color: "var(--ikaro-text-secondary)",
      marginBottom: 8,
    },
  },
  videoWrapper: {
    borderRadius: 10,
    overflow: "hidden",
    background: "#111",
    marginBottom: 20,
    boxShadow: "var(--ikaro-shadow-heavy)",
  },
  video: {
    width: "100%",
    maxHeight: 420,
    display: "block",
  },
  videoLoading: {
    display: "flex",
    flexDirection: "column",
    alignItems: "center",
    justifyContent: "center",
    gap: 12,
    padding: "40px 0",
    color: "var(--ikaro-text-muted)",
  },

  /* ─── Upload area ────────────────────────── */
  uploadArea: {
    border: "2px dashed var(--ikaro-border-default)",
    borderRadius: 10,
    padding: "20px 16px",
    textAlign: "center",
    cursor: "pointer",
    transition: "border-color 0.2s, background 0.2s",
    marginTop: 8,
    background: "var(--ikaro-bg-surface-secondary)",
    color: "var(--ikaro-text-secondary)",
    "&:hover": {
      borderColor: "var(--ikaro-orange-primary)",
      background: "var(--ikaro-bg-surface-hover)",
    },
  },
  uploadAreaActive: {
    borderColor: "var(--ikaro-orange-primary) !important",
    background: "var(--ikaro-orange-light) !important",
  },
  uploadFileName: {
    fontSize: "0.8rem",
    color: "var(--ikaro-text-secondary)",
    marginTop: 6,
  },

  /* ─── Quill editor ───────────────────────── */
  quillWrapper: {
    marginTop: 12,
    "& .ql-toolbar": {
      borderRadius: "8px 8px 0 0",
      border: "1px solid var(--ikaro-border-default) !important",
      background: "var(--ikaro-bg-surface-secondary)",
    },
    "& .ql-container": {
      borderRadius: "0 0 8px 8px",
      border: "1px solid var(--ikaro-border-default) !important",
      borderTop: "none !important",
      background: "var(--ikaro-bg-surface)",
      fontSize: "0.93rem",
      minHeight: 180,
    },
    "& .ql-editor": {
      minHeight: 180,
      color: "var(--ikaro-text-primary)",
      lineHeight: 1.7,
    },
    "& .ql-editor.ql-blank::before": {
      color: "var(--ikaro-text-muted)",
      fontStyle: "italic",
    },
    "& .ql-stroke": { stroke: "var(--ikaro-text-secondary) !important" },
    "& .ql-fill": { fill: "var(--ikaro-text-secondary) !important" },
    "& .ql-picker-label": { color: "var(--ikaro-text-secondary) !important" },
    "& .ql-picker-options": {
      background: "var(--ikaro-bg-elevated) !important",
      border: "1px solid var(--ikaro-border-default) !important",
    },
    "& .ql-picker-item": { color: "var(--ikaro-text-primary) !important" },
    "& .ql-active .ql-stroke": { stroke: "var(--ikaro-orange-primary) !important" },
    "& .ql-active .ql-fill": { fill: "var(--ikaro-orange-primary) !important" },
    "& .ql-active": { color: "var(--ikaro-orange-primary) !important" },
  },
  quillLabel: {
    fontSize: "0.75rem",
    color: "var(--ikaro-text-secondary)",
    marginBottom: 4,
    marginTop: 16,
    display: "block",
  },
}));

function useKbCanEdit(hasRole) {
  return useMemo(() => KB_EDIT_RULES.some((r) => hasRole("ADMIN", r)), [hasRole]);
}

export default function KnowledgeBasePage() {
  const classes = useStyles();
  const { token, hasRole } = useAuth();
  const { t } = useTranslation("knowledgeBase");
  const tRef = useRef(t);
  tRef.current = t;
  const canEdit = useKbCanEdit(hasRole);

  const [categories, setCategories] = useState([]);
  const [articles, setArticles] = useState([]);
  const [categoryFilter, setCategoryFilter] = useState(null);
  const [search, setSearch] = useState("");
  const [selectedArticle, setSelectedArticle] = useState(null);
  const [videoBlobUrl, setVideoBlobUrl] = useState(null);
  const [loading, setLoading] = useState(true);
  const [snack, setSnack] = useState({ open: false, message: "", severity: "info" });

  const [catDialog, setCatDialog] = useState({ open: false, editing: null });
  const [catForm, setCatForm] = useState({ name: "", sort_order: 0 });

  const [artDialog, setArtDialog] = useState({ open: false, editing: null });
  const [artForm, setArtForm] = useState({
    category_id: "",
    title: "",
    body: "",
    content_type: "text",
    videoFile: null,
  });

  const authJson = useCallback(
    () => ({
      Authorization: `Bearer ${token}`,
      "Content-Type": "application/json",
      "ngrok-skip-browser-warning": "true",
    }),
    [token]
  );

  const loadCategories = useCallback(async () => {
    const res = await fetch(`${API_BASE_URL}/knowledge_base/categories`, {
      headers: authJson(),
    });
    if (!res.ok) throw new Error("categories");
    setCategories(await res.json());
  }, [authJson]);

  const loadArticles = useCallback(async () => {
    const q =
      categoryFilter == null
        ? ""
        : `?category_id=${encodeURIComponent(categoryFilter)}`;
    const res = await fetch(`${API_BASE_URL}/knowledge_base/articles${q}`, {
      headers: authJson(),
    });
    if (!res.ok) throw new Error("articles");
    setArticles(await res.json());
  }, [authJson, categoryFilter]);

  const refreshAll = useCallback(async () => {
    setLoading(true);
    try {
      await loadCategories();
      await loadArticles();
    } catch {
      setSnack({ open: true, message: tRef.current("loadError"), severity: "error" });
    } finally {
      setLoading(false);
    }
  }, [loadArticles, loadCategories]);

  useEffect(() => {
    refreshAll();
  }, [refreshAll]);

  useEffect(() => {
    let cancelled = false;
    let createdUrl = null;
    const run = async () => {
      setVideoBlobUrl((prev) => { if (prev) URL.revokeObjectURL(prev); return null; });
      if (!selectedArticle || selectedArticle.content_type !== "video" || !selectedArticle.has_video) return;
      try {
        const res = await fetch(
          `${API_BASE_URL}/knowledge_base/articles/${selectedArticle.id}/video`,
          { headers: { Authorization: `Bearer ${token}`, "ngrok-skip-browser-warning": "true" } }
        );
        if (cancelled || !res.ok) return;
        const blob = await res.blob();
        if (cancelled) return;
        createdUrl = URL.createObjectURL(blob);
        if (cancelled) { URL.revokeObjectURL(createdUrl); return; }
        setVideoBlobUrl(createdUrl);
      } catch { if (!cancelled) setVideoBlobUrl(null); }
    };
    run();
    return () => {
      cancelled = true;
      if (createdUrl) URL.revokeObjectURL(createdUrl);
      setVideoBlobUrl((prev) => { if (prev) URL.revokeObjectURL(prev); return null; });
    };
  }, [selectedArticle, token]);

  /* ─── Helpers ─────────────────────────────────── */
  const categoryName = (id) => (categories.find((c) => c.id === id) || {}).name || "—";

  const quillModules = useMemo(() => ({
    toolbar: [
      [{ header: [1, 2, 3, false] }],
      ["bold", "italic", "underline", "strike"],
      [{ list: "ordered" }, { list: "bullet" }],
      [{ align: [] }],
      ["link", "image"],
      ["blockquote", "code-block"],
      ["clean"],
    ],
  }), []);

  const quillFormats = [
    "header",
    "bold", "italic", "underline", "strike",
    "list", "bullet",
    "align",
    "link", "image",
    "blockquote", "code-block",
  ];

  const filteredArticles = useMemo(() => {
    const s = search.trim().toLowerCase();
    return articles.filter((a) =>
      !s || a.title.toLowerCase().includes(s) || (a.body || "").toLowerCase().includes(s)
    );
  }, [articles, search]);

  /* ─── Categorias CRUD ─────────────────────────── */
  const openNewCategory = () => {
    setCatForm({ name: "", sort_order: categories.length });
    setCatDialog({ open: true, editing: null });
  };
  const openEditCategory = (c) => {
    setCatForm({ name: c.name, sort_order: c.sort_order });
    setCatDialog({ open: true, editing: c });
  };
  const saveCategory = async () => {
    try {
      const url = catDialog.editing
        ? `${API_BASE_URL}/knowledge_base/categories/${catDialog.editing.id}`
        : `${API_BASE_URL}/knowledge_base/categories`;
      const res = await fetch(url, {
        method: catDialog.editing ? "PATCH" : "POST",
        headers: authJson(),
        body: JSON.stringify({ name: catForm.name, sort_order: Number(catForm.sort_order) || 0 }),
      });
      if (!res.ok) throw new Error();
      setCatDialog({ open: false, editing: null });
      await refreshAll();
      setSnack({ open: true, message: t("savedCategory"), severity: "success" });
    } catch {
      setSnack({ open: true, message: t("saveError"), severity: "error" });
    }
  };
  const deleteCategory = async (c) => {
    if (!window.confirm(t("confirmDeleteCategory", { name: c.name }))) return;
    try {
      const res = await fetch(`${API_BASE_URL}/knowledge_base/categories/${c.id}`, {
        method: "DELETE",
        headers: authJson(),
      });
      if (!res.ok) {
        const err = await res.json().catch(() => ({}));
        throw new Error(err.detail || res.statusText);
      }
      if (categoryFilter === c.id) setCategoryFilter(null);
      await refreshAll();
      setSnack({ open: true, message: t("deletedCategory"), severity: "success" });
    } catch (e) {
      setSnack({ open: true, message: e.message || t("deleteError"), severity: "error" });
    }
  };

  /* ─── Artigos CRUD ─────────────────────────────── */
  const openNewArticle = () => {
    setArtForm({
      category_id: categoryFilter || (categories[0] && categories[0].id) || "",
      title: "",
      body: "",
      content_type: "text",
      videoFile: null,
    });
    setArtDialog({ open: true, editing: null });
  };
  const openEditArticle = (a) => {
    setArtForm({ category_id: a.category_id, title: a.title, body: a.body || "", content_type: a.content_type, videoFile: null });
    setArtDialog({ open: true, editing: a });
  };
  const saveArticle = async () => {
    try {
      if (artDialog.editing) {
        const a = artDialog.editing;
        if (artForm.content_type === "video" && a.content_type === "text" && !artForm.videoFile) {
          setSnack({ open: true, message: t("needVideoFile"), severity: "warning" });
          return;
        }
        if (artForm.content_type === "video" && artForm.videoFile) {
          const fd = new FormData();
          fd.append("video_file", artForm.videoFile);
          const rv = await fetch(`${API_BASE_URL}/knowledge_base/articles/${a.id}/replace_video`, {
            method: "POST",
            headers: { Authorization: `Bearer ${token}`, "ngrok-skip-browser-warning": "true" },
            body: fd,
          });
          if (!rv.ok) throw new Error();
        }
        const res = await fetch(`${API_BASE_URL}/knowledge_base/articles/${a.id}`, {
          method: "PATCH",
          headers: authJson(),
          body: JSON.stringify({ category_id: artForm.category_id, title: artForm.title, body: artForm.body, content_type: artForm.content_type }),
        });
        if (!res.ok) throw new Error();
      } else if (artForm.content_type === "text") {
        const res = await fetch(`${API_BASE_URL}/knowledge_base/articles`, {
          method: "POST",
          headers: authJson(),
          body: JSON.stringify({ category_id: artForm.category_id, title: artForm.title, body: artForm.body, content_type: "text" }),
        });
        if (!res.ok) throw new Error();
      } else {
        if (!artForm.videoFile) { setSnack({ open: true, message: t("needVideoFile"), severity: "warning" }); return; }
        const fd = new FormData();
        fd.append("category_id", artForm.category_id);
        fd.append("title", artForm.title);
        fd.append("body", artForm.body || "");
        fd.append("video_file", artForm.videoFile);
        const res = await fetch(`${API_BASE_URL}/knowledge_base/articles/upload_video`, {
          method: "POST",
          headers: { Authorization: `Bearer ${token}`, "ngrok-skip-browser-warning": "true" },
          body: fd,
        });
        if (!res.ok) throw new Error();
      }
      setArtDialog({ open: false, editing: null });
      await refreshAll();
      setSnack({ open: true, message: t("savedArticle"), severity: "success" });
    } catch {
      setSnack({ open: true, message: t("saveError"), severity: "error" });
    }
  };
  const deleteArticle = async (a) => {
    if (!window.confirm(t("confirmDeleteArticle", { title: a.title }))) return;
    try {
      const res = await fetch(`${API_BASE_URL}/knowledge_base/articles/${a.id}`, {
        method: "DELETE",
        headers: authJson(),
      });
      if (!res.ok) throw new Error();
      if (selectedArticle && selectedArticle.id === a.id) setSelectedArticle(null);
      await refreshAll();
      setSnack({ open: true, message: t("deletedArticle"), severity: "success" });
    } catch {
      setSnack({ open: true, message: t("deleteError"), severity: "error" });
    }
  };

  /* ─── Render ─────────────────────────────────── */
  return (
    <div className={classes.page}>

      {/* Header */}
      <div className={classes.pageHeader}>
        <div className={classes.pageHeaderLeft}>
          <div className={classes.pageIcon}><School /></div>
          <div>
            <Typography className={classes.pageTitle}>{t("title")}</Typography>
          </div>
        </div>
      </div>

      {loading ? (
        <Box display="flex" flex={1} justifyContent="center" alignItems="center">
          <CircularProgress />
        </Box>
      ) : (
        <div className={classes.body}>

          {/* Coluna 1 – Categorias */}
          <div className={classes.catPanel}>
            <div className={classes.catPanelHeader}>
              <Typography className={classes.catPanelTitle}>{t("categories")}</Typography>
              {canEdit && (
                <Tooltip title={t("newCategory")}>
                  <IconButton size="small" color="primary" onClick={openNewCategory}>
                    <Add fontSize="small" />
                  </IconButton>
                </Tooltip>
              )}
            </div>
            <div className={classes.catList}>
              <div
                className={`${classes.catItem} ${categoryFilter === null ? classes.catItemActive : ""}`}
                onClick={() => setCategoryFilter(null)}
              >
                <Typography className={classes.catItemLabel}>{t("allCategories")}</Typography>
                <ChevronRight style={{ fontSize: 16, opacity: 0.4 }} />
              </div>

              {categories.map((c) => (
                <div
                  key={c.id}
                  className={`${classes.catItem} ${categoryFilter === c.id ? classes.catItemActive : ""}`}
                  onClick={() => setCategoryFilter(c.id)}
                >
                  <Typography className={classes.catItemLabel}>{c.name}</Typography>
                  {canEdit ? (
                    <div className={classes.catItemActions}>
                      <IconButton
                        size="small"
                        onClick={(e) => { e.stopPropagation(); openEditCategory(c); }}
                      >
                        <Edit style={{ fontSize: 14 }} />
                      </IconButton>
                      <IconButton
                        size="small"
                        onClick={(e) => { e.stopPropagation(); deleteCategory(c); }}
                      >
                        <Delete style={{ fontSize: 14 }} />
                      </IconButton>
                    </div>
                  ) : (
                    <ChevronRight style={{ fontSize: 16, opacity: 0.4 }} />
                  )}
                </div>
              ))}
            </div>
          </div>

          {/* Coluna 2 – Lista de artigos */}
          <div className={classes.artPanel}>
            <div className={classes.artPanelHeader}>
              <Box display="flex" alignItems="center" justifyContent="space-between">
                <Typography style={{ fontWeight: 600, fontSize: "0.9rem" }}>
                  {t("articles")}
                  {filteredArticles.length > 0 && (
                    <span style={{ fontWeight: 400, color: "var(--ikaro-text-muted)", marginLeft: 6, fontSize: "0.78rem" }}>
                      ({filteredArticles.length})
                    </span>
                  )}
                </Typography>
                {canEdit && (
                  <Button
                    size="small"
                    startIcon={<Add />}
                    color="primary"
                    variant="contained"
                    disableElevation
                    onClick={openNewArticle}
                    disabled={categories.length === 0}
                    style={{ borderRadius: 8, textTransform: "none", fontSize: "0.78rem" }}
                  >
                    {t("newArticle")}
                  </Button>
                )}
              </Box>
              <TextField
                size="small"
                variant="outlined"
                placeholder={t("searchPlaceholder")}
                value={search}
                onChange={(e) => setSearch(e.target.value)}
                className={classes.artPanelSearch}
                fullWidth
                InputProps={{
                  startAdornment: (
                    <InputAdornment position="start">
                      <Search style={{ fontSize: 18, opacity: 0.5 }} />
                    </InputAdornment>
                  ),
                  style: { borderRadius: 8, fontSize: "0.83rem" },
                }}
              />
            </div>

            <div className={classes.artList}>
              {filteredArticles.length === 0 ? (
                <div className={classes.emptyState}>
                  <FolderOpen />
                  <Typography variant="body2">{t("noArticles")}</Typography>
                </div>
              ) : (
                filteredArticles.map((a) => (
                  <div
                    key={a.id}
                    className={`${classes.artCard} ${selectedArticle && selectedArticle.id === a.id ? classes.artCardActive : ""}`}
                    onClick={() => setSelectedArticle(a)}
                  >
                    <div className={classes.artCardTop}>
                      <Typography className={classes.artCardTitle}>{a.title}</Typography>
                      <div className={classes.artCardActions}>
                        {canEdit && (
                          <>
                            <IconButton size="small" onClick={(e) => { e.stopPropagation(); openEditArticle(a); }}>
                              <Edit style={{ fontSize: 14 }} />
                            </IconButton>
                            <IconButton size="small" onClick={(e) => { e.stopPropagation(); deleteArticle(a); }}>
                              <Delete style={{ fontSize: 14 }} />
                            </IconButton>
                          </>
                        )}
                      </div>
                    </div>
                    <div className={classes.artCardMeta}>
                      <Typography className={classes.artCardCat}>{categoryName(a.category_id)}</Typography>
                      <Chip
                        size="small"
                        label={a.content_type === "video" ? t("typeVideo") : t("typeText")}
                        icon={a.content_type === "video" ? <VideoLibrary style={{ fontSize: 12 }} /> : <Description style={{ fontSize: 12 }} />}
                        className={a.content_type === "video" ? classes.chipVideo : classes.chipText}
                        style={{ height: 20, fontSize: "0.68rem" }}
                      />
                    </div>
                  </div>
                ))
              )}
            </div>
          </div>

          {/* Coluna 3 – Visualização */}
          <div className={classes.viewPanel}>
            {!selectedArticle ? (
              <div className={classes.viewEmpty}>
                <MenuBook />
                <Typography variant="body1" style={{ fontWeight: 500 }}>
                  {t("selectArticle")}
                </Typography>
              </div>
            ) : (
              <div className={classes.viewContent}>
                <Typography className={classes.viewTitle}>{selectedArticle.title}</Typography>
                <div className={classes.viewMeta}>
                  <Chip
                    size="small"
                    label={categoryName(selectedArticle.category_id)}
                    style={{ background: "var(--ikaro-bg-surface-hover)", color: "var(--ikaro-text-secondary)", fontSize: "0.75rem" }}
                  />
                  <Chip
                    size="small"
                    label={selectedArticle.content_type === "video" ? t("typeVideo") : t("typeText")}
                    icon={
                      selectedArticle.content_type === "video"
                        ? <PlayCircleOutline style={{ fontSize: 14 }} />
                        : <Description style={{ fontSize: 14 }} />
                    }
                    className={selectedArticle.content_type === "video" ? classes.chipVideo : classes.chipText}
                    style={{ height: 22, fontSize: "0.75rem" }}
                  />
                </div>

                {selectedArticle.content_type === "video" ? (
                  <>
                    <div className={classes.videoWrapper}>
                      {videoBlobUrl ? (
                        <video
                          className={classes.video}
                          controls
                          controlsList="nodownload"
                          src={videoBlobUrl}
                        />
                      ) : (
                        <div className={classes.videoLoading}>
                          <CircularProgress size={28} />
                          <Typography variant="caption">{t("videoLoading")}</Typography>
                        </div>
                      )}
                    </div>
                    {selectedArticle.body ? (
                      <div className={`${classes.viewBody} ql-snow`}>
                        <div className="ql-editor" dangerouslySetInnerHTML={{ __html: selectedArticle.body }} />
                      </div>
                    ) : null}
                  </>
                  ) : (
                  selectedArticle.body
                    ? (
                      <div className={`${classes.viewBody} ql-snow`}>
                        <div className="ql-editor" dangerouslySetInnerHTML={{ __html: selectedArticle.body }} />
                      </div>
                    )
                    : <span style={{ color: "var(--ikaro-text-muted)", fontStyle: "italic" }}>{t("emptyBody")}</span>
                )}
              </div>
            )}
          </div>
        </div>
      )}

      {/* Dialog – Categoria */}
      <Dialog
        open={catDialog.open}
        onClose={() => setCatDialog({ open: false, editing: null })}
        maxWidth="xs"
        fullWidth
        PaperProps={{ style: { borderRadius: 14, background: "var(--ikaro-bg-surface)" } }}
      >
        <DialogTitle style={{ paddingBottom: 4 }}>
          {catDialog.editing ? t("editCategory") : t("newCategory")}
        </DialogTitle>
        <DialogContent>
          <TextField
            label={t("categoryName")}
            value={catForm.name}
            onChange={(e) => setCatForm((f) => ({ ...f, name: e.target.value }))}
            fullWidth
            variant="outlined"
            margin="normal"
            autoFocus
          />
          <TextField
            label={t("sortOrder")}
            type="number"
            value={catForm.sort_order}
            onChange={(e) => setCatForm((f) => ({ ...f, sort_order: e.target.value }))}
            fullWidth
            variant="outlined"
            margin="normal"
          />
        </DialogContent>
        <DialogActions style={{ padding: "8px 24px 16px" }}>
          <Button onClick={() => setCatDialog({ open: false, editing: null })} style={{ textTransform: "none" }}>
            {t("cancel")}
          </Button>
          <Button
            color="primary"
            variant="contained"
            disableElevation
            onClick={saveCategory}
            disabled={!catForm.name.trim()}
            style={{ textTransform: "none", borderRadius: 8 }}
          >
            {t("save")}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Dialog – Artigo */}
      <Dialog
        open={artDialog.open}
        onClose={() => setArtDialog({ open: false, editing: null })}
        maxWidth="sm"
        fullWidth
        PaperProps={{ style: { borderRadius: 14, background: "var(--ikaro-bg-surface)" } }}
      >
        <DialogTitle style={{ paddingBottom: 4 }}>
          {artDialog.editing ? t("editArticle") : t("newArticle")}
        </DialogTitle>
        <DialogContent>
          <TextField
            select
            label={t("category")}
            value={artForm.category_id}
            onChange={(e) => setArtForm((f) => ({ ...f, category_id: e.target.value }))}
            fullWidth
            variant="outlined"
            margin="normal"
          >
            {categories.map((c) => (
              <MenuItem key={c.id} value={c.id}>{c.name}</MenuItem>
            ))}
          </TextField>
          <TextField
            label={t("articleTitle")}
            value={artForm.title}
            onChange={(e) => setArtForm((f) => ({ ...f, title: e.target.value }))}
            fullWidth
            variant="outlined"
            margin="normal"
            autoFocus
          />
          <TextField
            select
            label={t("contentType")}
            value={artForm.content_type}
            onChange={(e) => setArtForm((f) => ({ ...f, content_type: e.target.value, videoFile: null }))}
            fullWidth
            variant="outlined"
            margin="normal"
          >
            <MenuItem value="text">{t("typeText")}</MenuItem>
            <MenuItem value="video">{t("typeVideo")}</MenuItem>
          </TextField>

          {artForm.content_type === "video" && (
            <label htmlFor="kb-video-upload" style={{ display: "block", marginTop: 8 }}>
              <div className={`${classes.uploadArea} ${artForm.videoFile ? classes.uploadAreaActive : ""}`}>
                <VideoLibrary style={{ fontSize: 28, opacity: 0.5, marginBottom: 4 }} />
                <Typography variant="body2" style={{ fontWeight: 500 }}>
                  {artDialog.editing ? t("replaceVideoHint") : t("uploadVideo")}
                </Typography>
                <Typography variant="caption" style={{ opacity: 0.55 }}>
                  .mp4 · .webm · .mov · .mkv
                </Typography>
                {artForm.videoFile && (
                  <Typography className={classes.uploadFileName}>
                    ✓ {artForm.videoFile.name}
                  </Typography>
                )}
              </div>
              <input
                id="kb-video-upload"
                type="file"
                accept="video/*,.mp4,.webm,.mov,.mkv"
                style={{ display: "none" }}
                onChange={(e) =>
                  setArtForm((f) => ({ ...f, videoFile: e.target.files && e.target.files[0] }))
                }
              />
            </label>
          )}

          <div className={classes.quillWrapper}>
            <Typography component="span" className={classes.quillLabel}>
              {t("body")}
            </Typography>
            <ReactQuill
              theme="snow"
              value={artForm.body}
              onChange={(html) => setArtForm((f) => ({ ...f, body: html }))}
              modules={quillModules}
              formats={quillFormats}
              placeholder={
                artForm.content_type === "video"
                  ? t("videoDescriptionPlaceholder")
                  : t("bodyPlaceholder")
              }
            />
          </div>
        </DialogContent>
        <DialogActions style={{ padding: "8px 24px 16px" }}>
          <Button onClick={() => setArtDialog({ open: false, editing: null })} style={{ textTransform: "none" }}>
            {t("cancel")}
          </Button>
          <Button
            color="primary"
            variant="contained"
            disableElevation
            onClick={saveArticle}
            disabled={!artForm.title.trim() || !artForm.category_id}
            style={{ textTransform: "none", borderRadius: 8 }}
          >
            {t("save")}
          </Button>
        </DialogActions>
      </Dialog>

      <Snackbar
        open={snack.open}
        autoHideDuration={5000}
        onClose={() => setSnack((s) => ({ ...s, open: false }))}
        anchorOrigin={{ vertical: "bottom", horizontal: "center" }}
      >
        <Alert severity={snack.severity} variant="filled">{snack.message}</Alert>
      </Snackbar>
    </div>
  );
}
